/**
 * CopilotPanel — Right sidebar with context-switching tabs
 *
 * Uses the TOTAL_UI_MATRIX for instant local lookups:
 * - Mode (from Activity Bar / Layout Store) determines tab STRUCTURE (the "Kit")
 * - Selection (from Graph Store) determines tab CONTENT (the "Data")
 *
 * TabConfig = f(mode, selection, tab) — pure lookup from local matrix.
 * No network calls - matrix is cached in the frontend.
 *
 * @component_type: UI Panel
 * @domain: Copilot/Assistant
 * @layer: presentation
 * @arch_component_type: container
 */
'use client';

import { useState, useEffect, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText, Sparkles, Zap, MessageSquare, Loader2,
  Home, BookOpen, Boxes, FlaskConical, Target, HelpCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useIsMobile } from '@/lib/hooks/use-is-mobile';
import { StrategyPanel } from './strategy-panel';
import type { LabelsDict } from '@/lib/api/types';
import { testIds } from '@/lib/test-utils/selectors';
import type { CopilotStrategy } from '@/lib/api/types';


// Icon resolver: backend sends Lucide icon names, we map to components.
// Same pattern as activity-bar.tsx — single icon registry for the app.
const ICON_MAP: Record<string, LucideIcon> = {
  FileText, Sparkles, Zap, MessageSquare, Home,
  BookOpen, Boxes, FlaskConical, Target, HelpCircle,
};

function getTabIcon(iconName: string): LucideIcon {
  return ICON_MAP[iconName] || FileText;
}


export function CopilotPanel() {
  const [activeTab, setActiveTab] = useState<string>('');

  // ─────────────────────────────────────────────────────────────────────────────
  // SHARED SELECTION: Single source of truth for the selection chain.
  // See viewport-selection.ts for priority documentation.
  // ─────────────────────────────────────────────────────────────────────────────
  const { mode, contentSelection: selection, entityId, entityType, projectId, selectedNode } = useViewportSelection();

  // ─────────────────────────────────────────────────────────────────────────────
  // MATRIX LOOKUP: Local lookup from Total Matrix (fetched once on app init)
  // ─────────────────────────────────────────────────────────────────────────────
  const { matrix, isLoading, error, getViewportConfig } = useTotalMatrixStore();
  // Tab labels {english: localized} for the active language. Read as a
  // primitive selector (stable reference) so we don't re-render on every
  // unrelated store change. Falls back to identity (English) when the
  // backend predates this field or carries no entry for a given label.
  const tabLabels: LabelsDict | null = useTotalMatrixStore((s) => s.tabLabels);
  const localize = (englishLabel: string) =>
    tabLabels?.[englishLabel] ?? englishLabel;

  // Per-form-factor visibility is matrix-driven via each tab's
  // `platforms` field. Chrome decisions stay in the matrix — this
  // component only checks "does my platform belong in this tab's
  // platforms list?" — never branches on a specific strategy name.
  // E.g. the Chat tab carries `platforms: ['mobile']` because desktop
  // reaches Chat via the EdgeStrip + ChatDrawer. See
  // klyde_edge/docs/copilot_chat_chrome_concept.md.
  const { isMobile } = useIsMobile();
  const currentPlatform = isMobile ? 'mobile' : 'desktop';

  // Get visible tabs from Total Matrix.
  // `matrix` is in the deps because `getViewportConfig` is a stable
  // store-method ref — without it the memo caches the first-render null
  // (matrix still in flight) and the tab strip stays empty.
  const visibleTabs = useMemo(() => {
    const config = getViewportConfig(mode, selection);
    const tabs = config?.copilot ?? [];
    return tabs.filter((t) => {
      // Missing/empty platforms list = legacy / fallback → render
      // everywhere. Explicit list = filter by membership.
      const platforms = t.platforms;
      if (!platforms || platforms.length === 0) return true;
      return platforms.includes(currentPlatform);
    });
  }, [mode, selection, getViewportConfig, currentPlatform, matrix]);

  // Reset to first visible tab when tabs change and current tab is hidden
  useEffect(() => {
    const isCurrentTabVisible = visibleTabs.some((t) => t.key === activeTab);
    if (!isCurrentTabVisible && visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  // Loading state (only on initial matrix fetch)
  if (isLoading || !matrix) {
    return (
      <div
        data-testid={testIds.copilot.container}
        className="h-full flex flex-col items-center justify-center bg-panel border-l border-panel-border"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-2">Loading...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        data-testid={testIds.copilot.container}
        className="h-full flex flex-col items-center justify-center bg-panel border-l border-panel-border p-4"
      >
        <p className="text-sm text-destructive">Failed to load copilot config</p>
        <p className="text-xs text-muted-foreground mt-1">{error}</p>
      </div>
    );
  }

  // No tabs visible
  if (visibleTabs.length === 0) {
    return (
      <div
        data-testid={testIds.copilot.container}
        className="h-full flex flex-col items-center justify-center bg-panel border-l border-panel-border p-4"
      >
        <p className="text-sm text-muted-foreground">No tabs available</p>
      </div>
    );
  }

  return (
    <div
      data-testid={testIds.copilot.container}
      className="h-full flex flex-col bg-panel border-l border-panel-border overflow-hidden"
    >
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="h-full flex flex-col overflow-hidden"
      >
        {/* Tab Header */}
        <div data-testid={testIds.copilot.header} className="border-b border-panel-border bg-muted/30 px-1">
          <TabsList className="h-9 bg-transparent gap-0">
            {visibleTabs.map((tab, idx) => {
              const Icon = getTabIcon(tab.icon ?? 'FileText');
              return (
                <TabsTrigger
                  key={tab.key}
                  data-testid={`copilot-tab-${tab.key}`}
                  value={tab.key}
                  className="gap-1.5 text-xs rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {localize(tab.label)}
                  {idx === 0 && selectedNode && (
                    <span className="ml-1 w-1.5 h-1.5 bg-primary rounded-full" />
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Tab Content */}
        {visibleTabs.map((tab) => (
          <TabsContent
            key={tab.key}
            value={tab.key}
            className="flex-1 m-0 data-[state=inactive]:hidden overflow-auto"
          >
            <StrategyPanel
              strategy={tab.strategy as CopilotStrategy}
              mode={mode}
              entityType={entityType}
              entityId={entityId}
              projectId={projectId}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
